import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Loader2, Zap, RefreshCw, Copy, Check, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface WebhookInfo {
  webhook_url: string | null;
  webhook_secret: string | null;
  webhook_activo: boolean;
  crear_ot_desde_presupuesto: boolean;
  vincular_material_desde_po: boolean;
  integracion_activa: boolean;
  eventos: Array<{ accion: string; resultado: string; mensaje: string; created_at: string }>;
}

export function HoldedAutomatizacionSection() {
  const [info, setInfo] = useState<WebhookInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async (action: 'get' | 'regenerate' | 'toggle' = 'get', extra: Record<string, unknown> = {}) => {
    const { data, error } = await supabase.functions.invoke('holded-webhook-setup', {
      body: { action, ...extra },
    });
    if (error) {
      const msg = (data as any)?.error || error.message;
      toast.error(msg || 'Error');
      return null;
    }
    setInfo(data as WebhookInfo);
    return data as WebhookInfo;
  };

  useEffect(() => {
    load('get').finally(() => setLoading(false));
  }, []);

  const regenerate = async () => {
    if (!confirm('¿Regenerar el secreto? Deberás actualizar la URL en Holded.')) return;
    setBusy(true);
    await load('regenerate');
    toast.success('Nuevo secreto generado');
    setBusy(false);
  };

  const toggle = async (field: 'activo' | 'crear_ot' | 'vincular_material', value: boolean) => {
    setBusy(true);
    await load('toggle', { [field]: value });
    setBusy(false);
  };

  const copy = async () => {
    if (!info?.webhook_url) return;
    await navigator.clipboard.writeText(info.webhook_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (!info?.integracion_activa) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" /> Automatización de pedidos de venta
            </CardTitle>
            <CardDescription>
              Cuando conviertes un presupuesto en pedido de venta en Holded, se crea automáticamente una OT en la app.
            </CardDescription>
          </div>
          {info.webhook_activo ? (
            <Badge className="bg-green-600">Activo</Badge>
          ) : (
            <Badge variant="outline">Inactivo</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-md border p-3">
          <div>
            <Label className="text-sm font-medium">Webhook activado</Label>
            <p className="text-xs text-muted-foreground">
              Habilita la recepción de eventos de Holded en este endpoint.
            </p>
          </div>
          <Switch
            checked={info.webhook_activo}
            disabled={busy}
            onCheckedChange={(v) => toggle('activo', v)}
          />
        </div>

        <div className="flex items-center justify-between rounded-md border p-3">
          <div>
            <Label className="text-sm font-medium">Crear OT al recibir un pedido de venta</Label>
            <p className="text-xs text-muted-foreground">
              Genera una OT en estado "creada" (sin técnico) para planificarla después.
            </p>
          </div>
          <Switch
            checked={info.crear_ot_desde_presupuesto}
            disabled={busy}
            onCheckedChange={(v) => toggle('crear_ot', v)}
          />
        </div>

        <div className="flex items-center justify-between rounded-md border p-3">
          <div>
            <Label className="text-sm font-medium">Vincular materiales desde pedidos de compra</Label>
            <p className="text-xs text-muted-foreground">
              Cuando crees un pedido de compra en Holded referenciando el pedido de venta, sus líneas aparecerán en la pestaña "Materiales" de la OT.
            </p>
          </div>
          <Switch
            checked={info.vincular_material_desde_po}
            disabled={busy}
            onCheckedChange={(v) => toggle('vincular_material', v)}
          />
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium">URL del webhook</Label>
          <div className="flex gap-2">
            <Input value={info.webhook_url || ''} readOnly className="font-mono text-xs" />
            <Button variant="outline" size="icon" onClick={copy} disabled={!info.webhook_url}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
            <Button variant="outline" onClick={regenerate} disabled={busy}>
              <RefreshCw className="h-4 w-4 mr-2" /> Regenerar
            </Button>
          </div>
          <div className="rounded-md bg-muted p-3 text-xs text-muted-foreground space-y-1">
            <p className="font-medium text-foreground">Configuración en Holded:</p>
            <ol className="list-decimal ml-4 space-y-0.5">
              <li>Entra en Holded → Settings → Developers → Webhooks.</li>
              <li>Añade un nuevo webhook con la URL de arriba.</li>
              <li>
                Suscribe <strong>los 6 eventos</strong> por separado (misma URL y token para todos). En Holded cada acción es una suscripción independiente — si sólo marcas "Crear", los cambios y borrados <strong>no llegarán</strong>:
                <ul className="list-disc ml-4 mt-1 space-y-0.5">
                  <li><strong>Pedido de venta</strong> · <strong>Crear</strong>, <strong>Actualizar</strong> y <strong>Eliminar</strong></li>
                  <li><strong>Pedido de compra</strong> · <strong>Crear</strong>, <strong>Actualizar</strong> y <strong>Eliminar</strong></li>
                </ul>
              </li>
              <li>
                Flujo: cliente acepta presupuesto → conviertes en pedido de venta (Raven crea OT) → creas 1..N pedidos de compra referenciando ese pedido de venta (Raven vincula las líneas como materiales de la OT). Cualquier cambio o borrado posterior en Holded se sincroniza automáticamente <em>siempre que los eventos "Actualizar" y "Eliminar" estén suscritos</em>.
              </li>
              <li>
                Si borras un pedido en Holded y aquí no aparece un evento <strong>webhook_recibido</strong> con acción <strong>deleted</strong>, Holded no está enviando esa suscripción de borrado a Raven.
              </li>
              <li>
                <strong>Importante:</strong> cada pedido de compra debe apuntar al pedido de venta (campo "De" en Holded) para que Raven pueda vincularlo automáticamente.
              </li>
              <li>Guarda. Validamos el token incluido en la URL automáticamente.</li>
            </ol>
            <a
              href="https://developers.holded.com/reference/webhooks"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              Documentación de webhooks Holded <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>

        {info.eventos.length > 0 && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Últimos eventos</Label>
            <div className="rounded-md border divide-y">
              {info.eventos.map((e, i) => (
                <div key={i} className="p-2 text-xs flex items-start gap-2">
                  <Badge
                    variant={e.resultado === 'ok' ? 'default' : e.resultado === 'error' ? 'destructive' : 'secondary'}
                    className="shrink-0"
                  >
                    {e.resultado}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="truncate">{e.mensaje}</p>
                    <p className="text-muted-foreground">
                      {new Date(e.created_at).toLocaleString('es-ES')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
